import { ImgID } from "../../image_loader/img_id.js";
import { ColorScheme } from "../common_ui_elements/color_scheme.js";
import { TextStyleScheme } from "../common_ui_elements/text_style_scheme.js";
import { UIImage } from "../common_ui_elements/ui_image.js";
import { UINineSliceElement } from "../common_ui_elements/ui_nine_slice_element.js";
import { MyTextAlign, MyTextVerticalAlign, UITextElement } from "../common_ui_elements/ui_text_element.js";
import { AbstractDialog } from "./abstract_dialog.js";
import { DialogManager } from "./dialog_manager.js";
import { ScreenManager, Screens } from "../../screen_manager/screen_manager.js";
import { StatisticsStorage } from "../../storage/statistics_storage.js";
import { MoreLivesDialog } from "./more_lives_dialog.js";
import { TransformScale } from "../../transforms/transform_scale.js";
import { Easing } from "../../transforms/transform_base.js";
import { Vector2 } from "../../common/vector2.js";
import { OverlayFactory } from "../overlay/overlay_factory.js";
import { OverlayId } from "../overlay/overlay_ids.js";
// Safe at runtime: level_pack_service only `import type`s from level_config, so
// this adds no edge back into that cycle.
import { LevelPackService } from "../../level_pack/level_pack_service.js";
import { Game } from "../../game.js";
import { BuyBoosterDialog } from "./buy_booster_dialog.js";
import { BuyBoosterType } from "./buy_booster_types.js";
import { CumulativeBoosterID } from "../../cumulative_boosters/cumulative_booster_id.js";
import { CumulativeBoosterService } from "../../cumulative_boosters/cumulative_booster_service.js";
import { UIElementHolder } from "../common_ui_elements/ui_element_holder.js";
import { LocalizationManager } from "../../localization/localization_manager.js";
import { completeBoosterTutorial, isBoosterTutorialCompleted } from "../../cumulative_boosters/booster_tutorial_progress.js";
import type { UIElement } from "../common_ui_elements/ui_element.js";
import { PersistentCache } from "../../offline/persistent_cache.js";

export enum PlayDialogState
{
  Play,
  TryAgain,
}

export enum PlayDialogDifficulty
{
  Common,
  Hard,
  SuperHard,
}

interface LevelStartBoosterSlotView
{
  background: UIImage;
  badge: UIImage;
  badge_size: number;
  count_text: UITextElement | null;
}

export interface PlayDialogParams
{
  level: number;
  state: PlayDialogState;
  difficulty: PlayDialogDifficulty;
  is_challenge: boolean;
  on_close?: () => void;
  on_retry?: () => void | Promise<void>;
}

export class PlayDialog extends AbstractDialog
{
  private static readonly PASSIVE_BOOSTER_ID = CumulativeBoosterID.Mine;
  private static readonly PASSIVE_BOOSTER_ICON = ImgID.UI_PassiveBoosterBomb;
  private static readonly DESIGN_W = 961.26;
  private static readonly DESIGN_H = 1335;
  private static readonly BG_W = 924;
  private static readonly BG_H = 1140;
  private static readonly BG_Y = 309;
  private static readonly BANNER_Y = 204;
  private static readonly BANNER_H = 346;
  private static readonly TUTORIAL_CURSOR_DESIGN_W = 150;
  private static readonly TUTORIAL_PRESS_PERIOD_MS = 1300;
  private static readonly TUTORIAL_PRESS_SCALE = 0.8;
  private static readonly SLOT_HINT_PENDING_KEY = "booster_slot_hint_pending_v1";
  private level: number;
  private state: PlayDialogState;
  private difficulty: PlayDialogDifficulty;
  private is_challenge: boolean;
  private on_close?: () => void;
  private on_retry?: () => void | Promise<void>;
  private title_text: UITextElement | null = null;
  private info_frame: UIImage | null = null;
  private is_retrying = false;
  private retry_inflight = false;
  private close_btn!: UIImage;
  private booster_tooltip: UIElementHolder | null = null;
  private booster_tooltip_dismiss_layer: UIElementHolder | null = null;
  private selected_level_start_booster_id: CumulativeBoosterID | null = null;
  private readonly level_start_booster_slots = new Map<CumulativeBoosterID, LevelStartBoosterSlotView>();
  private play_button: UIImage | null = null;
  private tutorial_cursor: UIImage | null = null;
  private tutorial_target: UIElement | null = null;
  private tutorial_started_at = 0;

  constructor(
    level: number,
    state: PlayDialogState = PlayDialogState.Play,
    difficulty: PlayDialogDifficulty = PlayDialogDifficulty.Common,
    is_challenge: boolean = false,
    on_close?: () => void,
    on_retry?: () => void | Promise<void>
  )
  {
    const body_w = LayoutScaler.widthFromDesign( PlayDialog.DESIGN_W );
    const body_h = LayoutScaler.heightFromDesign( PlayDialog.DESIGN_H );

    const body_x = ( DialogManager.getWidth() - body_w ) / 2;
    const body_y = ( DialogManager.getHeight() - body_h ) / 2
      - LayoutScaler.heightFromDesign( 140 );

    super( DialogManager.ctx, body_x, body_y, body_w, body_h );

    this.level = level;
    this.state = state;
    this.difficulty = difficulty;
    this.is_challenge = is_challenge;
    this.on_close = on_close;
    this.on_retry = on_retry;

    this.createElements();
  }

  private createElements(): void
  {
    this.createBackground();
    this.createTopImage();
    this.createTopBanner();
    this.createDifficultyLabel();
    this.createInfoFrame();
    this.createPlayButton();
    this.createCloseButton();
  }

  public static open(
    level: number,
    state: PlayDialogState = PlayDialogState.Play,
    difficulty: PlayDialogDifficulty = PlayDialogDifficulty.Common,
    is_challenge: boolean = false,
    on_close?: () => void,
    on_retry?: () => void | Promise<void>
  ): PlayDialog | null
  {
    if ( state === PlayDialogState.Play
      && !CumulativeBoosterService.isUnlocked( PlayDialog.PASSIVE_BOOSTER_ID, StatisticsStorage.load().level ) )
    {
      void PlayDialog.launch( level, state, is_challenge, null, on_retry );
      return null;
    }
    if ( state === PlayDialogState.Play && PlayDialog.isBoosterTutorialPending() )
    {
      OverlayFactory.openOverlay( OverlayId.NEW_BOOSTER_OVERLAY, {
        booster_id: PlayDialog.PASSIVE_BOOSTER_ID,
        on_claim: () =>
        {
          PlayDialog.claimBoosterTutorial();
          PlayDialog.open( level, state, difficulty, is_challenge, on_close, on_retry );
        },
      } );
      return null;
    }
    const dialog = PlayDialog.openDialog( level, state, difficulty, is_challenge, on_close, on_retry );
    if ( state === PlayDialogState.Play && PlayDialog.isSlotHintPending() )
      dialog.startBoosterTutorial();
    return dialog;
  }

  public static isBoosterTutorialPending(): boolean
  {
    return CumulativeBoosterService.getPropertiesConfig() !== null
      && CumulativeBoosterService.isUnlocked( PlayDialog.PASSIVE_BOOSTER_ID, StatisticsStorage.load().level )
      && !isBoosterTutorialCompleted( PlayDialog.PASSIVE_BOOSTER_ID );
  }

  public static claimBoosterTutorial(): void
  {
    completeBoosterTutorial( PlayDialog.PASSIVE_BOOSTER_ID );
    PersistentCache.write( PlayDialog.SLOT_HINT_PENDING_KEY, true );
  }

  public static isSlotHintPending(): boolean
  {
    return PersistentCache.read<boolean>( PlayDialog.SLOT_HINT_PENDING_KEY ) === true;
  }

  public static boosterTutorialId(): CumulativeBoosterID
  {
    return PlayDialog.PASSIVE_BOOSTER_ID;
  }

  private static openDialog(
    level: number,
    state: PlayDialogState,
    difficulty: PlayDialogDifficulty,
    is_challenge: boolean,
    on_close?: () => void,
    on_retry?: () => void | Promise<void>
  ): PlayDialog
  {
    const params: PlayDialogParams = { level, state, difficulty, is_challenge, on_close, on_retry };
    return DialogManager.openPooled( "PlayDialog", params, () => new PlayDialog( level, state, difficulty, is_challenge, on_close, on_retry ) ) as PlayDialog;
  }

  public override isPoolable(): boolean { return true; }

  public override onReuse( params: PlayDialogParams ): void
  {
    this.stopBoosterTutorial();
    this.is_retrying = false;
    this.retry_inflight = false;
    this.selected_level_start_booster_id = null;
    this.hideBoosterTooltip();
    this.on_close = params.on_close;
    this.on_retry = params.on_retry;
    this.is_challenge = params.is_challenge;

    const layout_changed = params.state !== this.state || params.difficulty !== this.difficulty;
    this.level = params.level;
    this.state = params.state;
    this.difficulty = params.difficulty;
    if ( layout_changed || !this.info_frame || !this.title_text )
    {
      this.rebuildChildren( () => this.createElements() );
      return;
    }

    this.title_text.setTextVariableParams( [ this.level.toString() ] );
    this.info_frame.cleanup();
    this.removeChild( this.info_frame );
    this.level_start_booster_slots.clear();
    this.createInfoFrame();
    this.invalidateSubtree();
  }

  private createBackground(): void
  {
    const design_bg_x = ( PlayDialog.DESIGN_W - PlayDialog.BG_W ) / 2;
    const design_seam_overlap = 4;
    const design_half_w = PlayDialog.BG_W / 2 + design_seam_overlap;
    const img_id = this.getBackgroundImgId();

    const half_w = LayoutScaler.widthFromDesign( design_half_w );
    const half_h = LayoutScaler.heightFromDesign( PlayDialog.BG_H );
    const left_x = LayoutScaler.xFromDesign( design_bg_x );
    const right_x = LayoutScaler.xFromDesign( design_bg_x + PlayDialog.BG_W - design_half_w );
    const bg_y = LayoutScaler.yFromDesign( PlayDialog.BG_Y );

    const left_half = new UIImage( this.ctx, left_x, bg_y, half_w, half_h, img_id );
    this.addChild( left_half );

    const right_half = new UIImage( this.ctx, right_x, bg_y, half_w, half_h, img_id );
    right_half.setFlipX( true );
    this.addChild( right_half );
  }

  private getBackgroundImgId(): ImgID
  {
    switch ( this.difficulty )
    {
      case PlayDialogDifficulty.Hard:      return ImgID.UI_PlayDialogBgHalfHard;
      case PlayDialogDifficulty.SuperHard: return ImgID.UI_PlayDialogBgHalfSuperHard;
      default:                             return ImgID.UI_PlayDialogBgHalfCommon;
    }
  }

  private createDifficultyLabel(): void
  {
    if ( this.difficulty === PlayDialogDifficulty.Common )
      return;

    const is_super_hard = this.difficulty === PlayDialogDifficulty.SuperHard;
    const design_pill_center_y = 266;
    const design_label_w = is_super_hard ? 400 : 340;
    const design_label_h = 83;
    const design_label_x = ( PlayDialog.DESIGN_W - design_label_w ) / 2;
    const design_label_y = PlayDialog.BANNER_Y + design_pill_center_y - design_label_h / 2 - LayoutScaler.yFromDesign( 10 );
    const design_font_size = 60;
    const design_outline_w = 10;

    const label = new UITextElement(
      this.ctx,
      LayoutScaler.xFromDesign( design_label_x ),
      LayoutScaler.yFromDesign( design_label_y ),
      LayoutScaler.widthFromDesign( design_label_w ),
      LayoutScaler.heightFromDesign( design_label_h ),
      {
        text: is_super_hard ? "Super Hard!" : "Hard!",
        font_size: LayoutScaler.textSizeFromDesign( design_font_size ),
        font_weight: 900,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.geologica,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        outline_width: LayoutScaler.textBorderSizeFromDesign( design_outline_w ),
        outline_color: ColorScheme.settings_title_outline,
        outline_offset_y: LayoutScaler.yFromDesign( 3 ),
        scale_to_width: true,
        auto_wrap: false,
      }
    );
    this.addChild( label );
  }

  private createTopBanner(): void
  {
    const design_banner_x = 0;
    const design_banner_y = PlayDialog.BANNER_Y;
    const design_banner_h = PlayDialog.BANNER_H;
    const design_title_x = 222;
    const design_title_y = 94;
    const design_title_w = 518;
    const design_title_h = 123;
    const design_font_size = 88;
    const design_outline_w = 10;

    const banner_w = LayoutScaler.widthFromDesign( PlayDialog.DESIGN_W );
    const banner_h = LayoutScaler.heightFromDesign( design_banner_h );

    const img = new UIImage(
      this.ctx,
      LayoutScaler.xFromDesign( design_banner_x ),
      LayoutScaler.yFromDesign( design_banner_y ),
      banner_w,
      banner_h,
      this.getRibbonImgId()
    );
    this.addChild( img );

    const title_h = LayoutScaler.heightFromDesign( design_title_h );
    const title_w = LayoutScaler.widthFromDesign( design_title_w );

    const title = this.title_text = new UITextElement(
      this.ctx,
      LayoutScaler.xFromDesign( design_title_x ),
      LayoutScaler.yFromDesign( design_title_y ),
      title_w,
      title_h,
      {
        localization_key: "PLAY_DIALOG.TITLE",
        text_variable_params: [ this.level.toString() ],
        font_size: LayoutScaler.textSizeFromDesign( design_font_size ),
        font_weight: 900,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.geologica,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        outline_width: LayoutScaler.textBorderSizeFromDesign( design_outline_w ),
        outline_color: ColorScheme.play_dialog_title_outline,
        outline_offset_y: LayoutScaler.yFromDesign( 3 ),
        scale_to_width: true,
        auto_wrap: false,
        shadow_color: ColorScheme.play_dialog_title_shadow,
        shadow_blur: LayoutScaler.widthFromDesign( 10 ),
        shadow_offset_y: LayoutScaler.yFromDesign( 5 ),
        shadow_offset_x: LayoutScaler.xFromDesign( 0 ),
      }
    );
    img.addChild( title );
  }

  private getRibbonImgId(): ImgID
  {
    switch ( this.difficulty )
    {
      case PlayDialogDifficulty.Hard:      return ImgID.UI_GameStartRibbonHard;
      case PlayDialogDifficulty.SuperHard: return ImgID.UI_GameStartRibbonSuperHard;
      default:                             return ImgID.UI_GoldenRibbon;
    }
  }

  private createTopImage(): void
  {
    const design_img_y = 0;
    const design_img_w = 733;
    const design_img_h = 283;
    const design_img_x = ( PlayDialog.DESIGN_W - design_img_w ) / 2;

    const img = new UIImage(
      this.ctx,
      LayoutScaler.xFromDesign( design_img_x ),
      LayoutScaler.yFromDesign( design_img_y ),
      LayoutScaler.widthFromDesign( design_img_w ),
      LayoutScaler.heightFromDesign( design_img_h ),
      ImgID.UI_PlayDialogTop
    );
    this.addChild( img );
  }

  private createInfoFrame(): void
  {
    const design_frame_w = 692;
    const design_frame_h = 414;
    const design_frame_x = ( PlayDialog.DESIGN_W - design_frame_w ) / 2;
    const design_frame_y = 576;

    const frame_w = LayoutScaler.widthFromDesign( design_frame_w );
    const frame_h = LayoutScaler.heightFromDesign( design_frame_h );
    const frame_x = LayoutScaler.xFromDesign( design_frame_x );
    const frame_y = LayoutScaler.yFromDesign( design_frame_y );

    const frame = this.info_frame = new UIImage( this.ctx, frame_x, frame_y, frame_w, frame_h, ImgID.UI_GameStartContentBg );
    this.addChild( frame );

    const design_title_w = 520;
    const design_title_h = 70;
    const design_title_x = ( design_frame_w - design_title_w ) / 2;
    const design_title_y = 28;

    const title_x = LayoutScaler.xFromDesign( design_title_x );
    const title_y = LayoutScaler.yFromDesign( design_title_y );
    const title_w = LayoutScaler.widthFromDesign( design_title_w );
    const title_h = LayoutScaler.heightFromDesign( design_title_h );

    const title = new UITextElement(
      this.ctx, title_x, title_y, title_w, title_h,
      {
        localization_key: "PLAY_DIALOG.TEXT",
        font_size: LayoutScaler.textSizeFromDesign( 51 ),
        font_weight: 900,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.geologica,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 10 ),
        outline_color: ColorScheme.play_dialog_text_outline,
        outline_offset_y: LayoutScaler.yFromDesign( 2 ),
        scale_to_width: true,
        auto_wrap: false,
      }
    );
    frame.addChild( title );

    const slot_size = 190;
    const slot_x = ( design_frame_w - slot_size ) / 2;
    const slot_y = 130;
    this.createLevelStartBoosterSlot( frame, slot_x, slot_y, slot_size, PlayDialog.PASSIVE_BOOSTER_ID );
  }

  private createLevelStartBoosterSlot(
    frame: UIImage,
    x: number,
    y: number,
    size: number,
    booster_id: CumulativeBoosterID
  ): void
  {
    const unlock_level = CumulativeBoosterService.getUnlockLevel( booster_id );
    const is_unlocked = CumulativeBoosterService.isUnlocked( booster_id, StatisticsStorage.load().level );
    const slot = new UIImage(
      this.ctx,
      LayoutScaler.xFromDesign( x ),
      LayoutScaler.yFromDesign( y ),
      LayoutScaler.widthFromDesign( size ),
      LayoutScaler.heightFromDesign( size ),
      ImgID.UI_PassiveBoosterDisabled,
      undefined,
      true
    );
    frame.addChild( slot );

    if ( !is_unlocked )
    {
      const locked_icon = new UIImage(
        this.ctx,
        0,
        LayoutScaler.yFromDesign( -22 ),
        LayoutScaler.widthFromDesign( size ),
        LayoutScaler.heightFromDesign( size ),
        ImgID.UI_GameStartLockedBooster
      );
      slot.addChild( locked_icon, true );
      this.createLockedLevelLabel( slot, size, unlock_level );
      slot.setOnClick( () => this.showBoosterTooltip(
        slot,
        `${LocalizationManager.t( "BOOSTER.AVAILABLE" )} ${unlock_level}`
      ) );
      return;
    }

    const icon_design_size = 190;
    const icon = new UIImage(
      this.ctx,
      LayoutScaler.xFromDesign( ( size - icon_design_size ) / 2 ),
      LayoutScaler.yFromDesign( -12 ),
      LayoutScaler.widthFromDesign( icon_design_size ),
      LayoutScaler.heightFromDesign( icon_design_size ),
      PlayDialog.PASSIVE_BOOSTER_ICON
    );
    slot.addChild( icon, true );

    const badge_design_size = 80;
    const badge_size = LayoutScaler.widthFromDesign( badge_design_size );
    const badge = new UIImage(
      this.ctx,
      LayoutScaler.xFromDesign( ( size - badge_design_size ) / 2 ),
      LayoutScaler.yFromDesign( 145 ),
      badge_size,
      LayoutScaler.heightFromDesign( badge_design_size ),
      ImgID.UI_PassiveBoosterCountBadge,
      undefined,
      true
    );
    slot.addChild( badge, true );

    const view: LevelStartBoosterSlotView = {
      background: slot,
      badge,
      badge_size,
      count_text: null,
    };
    this.level_start_booster_slots.set( booster_id, view );
    this.refreshLevelStartBoosterSelection();

    slot.setOnClick( () =>
    {
      if ( CumulativeBoosterService.inventory.getCount( booster_id ) <= 0 )
      {
        void this.openBuyBoosterDialog(
          BuyBoosterType.Firework,
          booster_id,
          () => this.refreshLevelStartBoosterSelection()
        );
        return;
      }
      this.selected_level_start_booster_id = this.selected_level_start_booster_id === booster_id
        ? null
        : booster_id;
      this.refreshLevelStartBoosterSelection();
      if ( this.tutorial_target === slot && this.selected_level_start_booster_id === booster_id && this.play_button )
        this.pointTutorialAt( this.play_button );
    } );
  }

  private refreshLevelStartBoosterSelection(): void
  {
    for ( const [ id, view ] of this.level_start_booster_slots )
    {
      const count = CumulativeBoosterService.inventory.getCount( id );
      const selected = this.selected_level_start_booster_id === id && count > 0;

      view.background.setImageId(
        selected ? ImgID.UI_PassiveBoosterEnabled : ImgID.UI_PassiveBoosterDisabled
      );
      view.badge.setImageId(
        selected
          ? ImgID.UI_PassiveBoosterSelectedBadge
          : count > 0
            ? ImgID.UI_PassiveBoosterCountBadge
            : ImgID.UI_PassiveBoosterPlusBadge
      );

      if ( view.count_text )
      {
        view.badge.removeChild( view.count_text );
        view.count_text = null;
      }
      if ( count > 0 && !selected )
        view.count_text = this.createBoosterCountText( view.badge, view.badge_size, count );
    }
    this.invalidateSubtree();
  }

  private async openBuyBoosterDialog(
    booster_type: BuyBoosterType,
    booster_id: CumulativeBoosterID,
    on_inventory_changed?: () => void
  ): Promise<void>
  {
    const offer = await CumulativeBoosterService.getOrLoadFirstPriceOffer( booster_id );
    if ( offer === null )
    {
      console.warn( `[Boosters] No price offer for booster ${booster_id}` );
      return;
    }

    BuyBoosterDialog.open( {
      booster_type,
      booster_id,
      offer,
      source: "game_start",
      on_buy: () => on_inventory_changed?.(),
      on_reward: () => on_inventory_changed?.(),
    } );
  }

  private createLockedLevelLabel(
    booster_bg: UIImage,
    design_slot_size: number,
    unlock_level: number
  ): void
  {
    const label = new UITextElement(
      this.ctx, 0, LayoutScaler.yFromDesign( design_slot_size - 28 ),
      LayoutScaler.widthFromDesign( design_slot_size ), LayoutScaler.heightFromDesign( 50 ),
      {
        text: `Lv.${unlock_level}`, font_size: LayoutScaler.textSizeFromDesign( 38 ), font_weight: 900,
        text_color: ColorScheme.white, text_style: TextStyleScheme.geologica,
        text_align: MyTextAlign.Center, vertical_align: MyTextVerticalAlign.Center,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 8 ),
        outline_color: ColorScheme.continue_overlay_caption_outline,
      }
    );
    booster_bg.addChild( label, true );
  }

  private showBoosterTooltip( booster_bg: UIImage, tooltip_text: string ): void
  {
    this.hideBoosterTooltip();

    const tape_w = LayoutScaler.widthFromDesign( 675 );
    const tape_h = LayoutScaler.heightFromDesign( 200 );
    const bottom_w = LayoutScaler.widthFromDesign( 110.46 );
    const bottom_h = LayoutScaler.heightFromDesign( 77.12 );
    const side_margin = LayoutScaler.widthFromDesign( 33 );
    const dialog_rect = this.getAbsoluteRect();
    const slot_rect = booster_bg.getAbsoluteRect();
    const slot_center_x = slot_rect.x - dialog_rect.x + slot_rect.width / 2;

    const tape_x = Math.max(
      side_margin,
      Math.min( slot_center_x - tape_w / 2, this.rect.getW() - side_margin - tape_w )
    );
    const tape_y = slot_rect.y - dialog_rect.y - LayoutScaler.heightFromDesign( 219.75 );
    const bottom_x = Math.max(
      0,
      Math.min( slot_center_x - tape_x - bottom_w / 2, tape_w - bottom_w )
    );
    const bottom_y = LayoutScaler.heightFromDesign( 167.68 );

    const dismiss_layer = new UIElementHolder(
      this.ctx,
      0,
      0,
      this.rect.getW(),
      this.rect.getH()
    );
    dismiss_layer.setOnClick( () => this.hideBoosterTooltip(), true );

    const tooltip = new UIElementHolder(
      this.ctx,
      tape_x,
      tape_y,
      tape_w,
      Math.max( tape_h, bottom_y + bottom_h )
    );
    tooltip.setClickable( false );

    const tape = new UINineSliceElement(
      this.ctx,
      0,
      0,
      tape_w,
      tape_h,
      ImgID.UI_GameBoosterLockedTape,
      "booster_tape"
    );
    tooltip.addChild( tape );

    const text = new UITextElement(
      this.ctx,
      LayoutScaler.xFromDesign( 30 ),
      LayoutScaler.yFromDesign( 20 ),
      tape_w - LayoutScaler.widthFromDesign( 60 ),
      tape_h - LayoutScaler.heightFromDesign( 40 ),
      {
        text: tooltip_text,
        font_size: LayoutScaler.textSizeFromDesign( 52 ),
        font_weight: 900,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.geologica,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 10 ),
        outline_color: ColorScheme.continue_overlay_caption_outline,
        fit_font_size: true,
        min_font_size: LayoutScaler.textSizeFromDesign( 30 ),
        line_height: 0.95,
        auto_wrap: true,
      }
    );
    tape.addChild( text );

    const bottom = new UIImage(
      this.ctx,
      bottom_x,
      bottom_y,
      bottom_w,
      bottom_h,
      ImgID.UI_GameBoosterBottom
    );
    tooltip.addChild( bottom );

    this.booster_tooltip_dismiss_layer = dismiss_layer;
    this.addChild( dismiss_layer );
    this.booster_tooltip = tooltip;
    this.addChild( tooltip );
    this.invalidateSubtree();
  }

  private hideBoosterTooltip(): void
  {
    if ( this.booster_tooltip )
      this.removeChild( this.booster_tooltip );
    if ( this.booster_tooltip_dismiss_layer )
      this.removeChild( this.booster_tooltip_dismiss_layer );

    this.booster_tooltip = null;
    this.booster_tooltip_dismiss_layer = null;
    this.invalidateSubtree();
  }

  private createBoosterCountText(
    badge: UIImage,
    badge_size: number,
    count: number
  ): UITextElement
  {
    const design_font_size = 40;
    const design_shadow_blur = 6;
    const design_shadow_offset_y = 3;

    const count_text = new UITextElement(
      this.ctx, 0, 0, badge_size, badge_size,
      {
        text: count.toString(),
        font_size: LayoutScaler.textSizeFromDesign( design_font_size ),
        font_weight: 900,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.geologica,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        shadow_blur: LayoutScaler.widthFromDesign( design_shadow_blur ),
        shadow_color: ColorScheme.black_transparent,
        shadow_offset_y: LayoutScaler.yFromDesign( design_shadow_offset_y ),
        scale_to_width: true,
        auto_wrap: false,
      }
    );
    badge.addChild( count_text, true );
    return count_text;
  }

  private createPlayButton(): void
  {
    const design_btn_w = 712;
    const design_btn_h = 326;
    const design_btn_y = 1050;
    const design_btn_x = ( PlayDialog.DESIGN_W - design_btn_w ) / 2;

    const btn_w = LayoutScaler.widthFromDesign( design_btn_w );
    const btn_h = LayoutScaler.heightFromDesign( design_btn_h );
    const btn_x = LayoutScaler.xFromDesign( design_btn_x );
    const btn_y = LayoutScaler.yFromDesign( design_btn_y );

    const btn = new UIImage(
      this.ctx,
      btn_x,
      btn_y,
      btn_w,
      btn_h,
      ImgID.UI_GreenFrameBtn,
      undefined,
      true
    );

    const design_text_font_size = 98;

    const text_options = {
      font_size: LayoutScaler.textSizeFromDesign( design_text_font_size ),
      font_weight: 900,
      text_color: ColorScheme.white,
      text_style: TextStyleScheme.geologica,
      text_align: MyTextAlign.Center,
      vertical_align: MyTextVerticalAlign.Center,
      scale_to_width: true,
      auto_wrap: false,
      outline_width: LayoutScaler.textBorderSizeFromDesign( 9 ),
      outline_color: ColorScheme.play_dialog_button_outline,
      ...TextStyleScheme.buttonShadow(),
    };

    const MAGIC_NUM_CORRECTION = 0.7;
    const design_try_again_font_size = 64;
    const is_try_again = this.state === PlayDialogState.TryAgain;
    const play_text_w = btn_w * MAGIC_NUM_CORRECTION;
    const play_font_size = is_try_again
      ? LayoutScaler.textSizeFromDesign( design_try_again_font_size )
      : text_options.font_size;

    const play_text = new UITextElement(
      this.ctx, ( btn_w - play_text_w ) / 2, 0, play_text_w, btn_h,
      {
        localization_key: is_try_again ? "TRY_AGAIN" : "PLAY",
        ...text_options,
        font_size: play_font_size,
        auto_wrap: is_try_again,
        scale_to_width: !is_try_again,
        line_height: is_try_again ? 0.95 : 1.1,
      }
    );
    btn.addChild( play_text, true );

    btn.setOnClick( async () =>
    {
      if ( this.retry_inflight )
        return;

      this.retry_inflight = true;
      this.is_retrying = true;
      if ( this.tutorial_target && this.selected_level_start_booster_id === PlayDialog.PASSIVE_BOOSTER_ID )
        PersistentCache.remove( PlayDialog.SLOT_HINT_PENDING_KEY );
      this.stopBoosterTutorial();
      await PlayDialog.launch( this.level, this.state, this.is_challenge, this.selected_level_start_booster_id, this.on_retry );
    } );

    this.addChild( btn );
    this.play_button = btn;
  }

  private startBoosterTutorial(): void
  {
    const slot = this.level_start_booster_slots.get( PlayDialog.PASSIVE_BOOSTER_ID )?.background;
    if ( slot )
      this.pointTutorialAt( slot );
  }

  private pointTutorialAt( target: UIElement ): void
  {
    this.stopBoosterTutorial();
    const cursor_w = LayoutScaler.widthFromDesign( PlayDialog.TUTORIAL_CURSOR_DESIGN_W );
    const cursor = new UIImage( this.ctx, 0, 0, cursor_w, cursor_w * 183 / 159, ImgID.IconTutorialCursor );
    cursor.setClickable( false );
    this.addChild( cursor );
    this.tutorial_cursor = cursor;
    this.tutorial_target = target;
    this.tutorial_started_at = performance.now();
    this.updateTutorialCursor( this.tutorial_started_at );
  }

  private stopBoosterTutorial(): void
  {
    if ( this.tutorial_cursor )
      this.removeChild( this.tutorial_cursor );
    this.tutorial_cursor = null;
    this.tutorial_target = null;
  }

  private updateTutorialCursor( now: number ): void
  {
    const cursor = this.tutorial_cursor;
    const target = this.tutorial_target;
    if ( !cursor || !target ) return;

    const target_pos = target.getAbsolutePosition();
    const own_pos = this.getAbsolutePosition();
    const rest_x = target_pos.x - own_pos.x + target.getRect().getW2();
    const rest_y = target_pos.y - own_pos.y + target.getRect().getH() * 0.28;

    const t = ( ( now - this.tutorial_started_at ) % PlayDialog.TUTORIAL_PRESS_PERIOD_MS ) / PlayDialog.TUTORIAL_PRESS_PERIOD_MS;
    const scale = 1 - ( 1 - PlayDialog.TUTORIAL_PRESS_SCALE ) * Math.sin( t * Math.PI );
    const shift = 1 - scale;
    cursor.setPixelPos( new Vector2(
      rest_x - shift * cursor.getRect().getW2(),
      rest_y - shift * cursor.getRect().getH2()
    ), true );
    cursor.setScaleImmediate( new Vector2( scale, scale ) );
    this.invalidateSubtree();
  }

  public override update( now: number ): void
  {
    super.update( now );
    this.updateTutorialCursor( now );
  }

  private static async launch(
    level: number,
    state: PlayDialogState,
    is_challenge: boolean,
    level_start_booster_id: CumulativeBoosterID | null,
    on_retry?: () => void | Promise<void>
  ): Promise<void>
  {
    const show_loading_screen = state === PlayDialogState.Play;
    if ( show_loading_screen )
      Game.instance.showLoadingGameScreen();
    DialogManager.closeAllDialogs();

    try
    {
      if ( show_loading_screen )
        await Game.instance.waitForLoadingGameScreenPaint();
      await on_retry?.();

      if ( state === PlayDialogState.TryAgain && !is_challenge && StatisticsStorage.load().hearts <= 0 )
      {
        await ScreenManager.showScreen( Screens.MENU );
        MoreLivesDialog.open();
        return;
      }
      if ( state === PlayDialogState.Play && !is_challenge && StatisticsStorage.load().new_goal_pending )
      {
        StatisticsStorage.update( { new_goal_pending: false } );
        await ScreenManager.showScreen( Screens.GAME, {
          level,
          is_challenge,
          level_start_booster_id: level_start_booster_id ?? undefined,
          defer_level_start_booster: true,
        } );
        OverlayFactory.openOverlay( OverlayId.NEW_GOAL_OVERLAY, {
          unlocks: LevelPackService.getUnlocks( level ),
          onClose: () =>
          {
            void ScreenManager.getCurrentScreenInstance()?.afterIntroOverlay?.();
          },
        } );
        return;
      }
      await ScreenManager.showScreen( Screens.GAME, {
        level,
        is_challenge,
        level_start_booster_id: level_start_booster_id ?? undefined,
      } );
    }
    finally
    {
      if ( show_loading_screen )
      {
        await Game.instance.waitForMinimumLoadingGameScreenDuration();
        Game.instance.hideLoadingGameScreen();
      }
    }
  }

  private createCloseButton(): void
  {
    const design_btn_size = 110;
    const design_btn_y = 216;
    const design_btn_x = 844;

    const btn_w = LayoutScaler.widthFromDesign( design_btn_size );
    const btn_h = LayoutScaler.heightFromDesign( design_btn_size );

    const btn = new UIImage(
      this.ctx,
      LayoutScaler.xFromDesign( design_btn_x ),
      LayoutScaler.yFromDesign( design_btn_y ),
      btn_w,
      btn_h,
      ImgID.UI_SettingsDialogCloseBtn,
      undefined,
      true
    );
    btn.setOnClick( () => this.close() );
    this.addChild( btn );
    this.close_btn = btn;
  }

  protected override playAppearAnimation(
    from_scale      = 0.7,
    overshoot_scale = 1.1,
    final_scale     = 1.0,
    scale_up_duration = 136,
    settle_duration   = 96
  ): void
  {
    this.close_btn.setScale( new Vector2( 0, 0 ) );

    super.playAppearAnimation( from_scale, overshoot_scale, final_scale, scale_up_duration, settle_duration );

    this.popCloseButtonAfterAppear( this.close_btn, scale_up_duration + settle_duration );
  }

  public override onDialogWillClose(): void
  {
    this.stopBoosterTutorial();
    if ( this.is_retrying )
      return;

    this.on_close?.();
  }

  public name(): string
  {
    return "PlayDialog";
  }

  public render(): void
  {
    if ( !this.shouldBeRendered() )
      return;

    this.startRender();
    this.finishRender();
  }
}
